/* Branding — the name, the logo and the colours the board is drawn in.
 *
 * The same three settings the salon can change for itself under Settings →
 * Branding in the console. They are here because setting a customer up is
 * mostly this, and doing it for them over a call beats talking somebody through
 * a colour picker.
 *
 * Two things this page can do that the console's own page cannot, and both are
 * the point of the app:
 *
 *   unset a key   — deletes the row rather than writing a default into it, so
 *                   the salon is genuinely back to a fresh install for that
 *                   setting. See `clearSetting` in js/store.js for why those are
 *                   not the same thing.
 *   set a preset  — including one this build does not know about, through the
 *                   raw editor on the Advanced page. The list below is a mirror
 *                   of the console's and is allowed to fall behind it; see the
 *                   header of js/rules/theme-presets.js.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { themePresets } from '../rules/theme-presets.js';
import { ConfirmButton } from '../components/confirm-button.js';

/* The console's own limits, restated. A logo is stored as a data URI inside the
 * settings row and mirrored into every station's localStorage, which is what
 * sets the size cap — see the LOGO block in ../../../js/theme.js. Uploading
 * something larger from here would fit in the database and then fail to fit on
 * the tablets, which is a bug nobody would connect to this screen. */
var LOGO = {
  maxBytes: 200 * 1024,
  types: ['image/png', 'image/jpeg', 'image/webp'],
  typeLabel: 'PNG, JPEG or WebP',
  maxW: 1200, maxH: 400, minH: 32
};

function sizeLabel(bytes) {
  return bytes >= 1024 * 1024
    ? (bytes / 1024 / 1024).toFixed(1) + ' MB'
    : Math.round(bytes / 1024) + ' KB';
}

// Resolves { dataUrl, width, height } or rejects with a sentence meant to be
// shown as-is. Same checks and same order as the console's readLogo.
function readLogo(file) {
  return new Promise(function (resolve, reject) {
    if (!file) return reject(new Error('No file chosen.'));
    if (LOGO.types.indexOf(file.type) === -1) {
      return reject(new Error('That is a ' + (file.type || 'unknown') +
        ' file — use ' + LOGO.typeLabel + '.'));
    }
    if (file.size > LOGO.maxBytes) {
      return reject(new Error('That file is ' + sizeLabel(file.size) +
        '. The limit is ' + sizeLabel(LOGO.maxBytes) + '.'));
    }
    var reader = new FileReader();
    reader.onerror = function () { reject(new Error('That file could not be read.')); };
    reader.onload = function () {
      var dataUrl = String(reader.result || '');
      var img = new Image();
      img.onerror = function () { reject(new Error('That file is not a readable image.')); };
      img.onload = function () {
        var w = img.naturalWidth, h = img.naturalHeight;
        if (!w || !h) return reject(new Error('That image has no size.'));
        if (w > LOGO.maxW || h > LOGO.maxH) {
          return reject(new Error('That image is ' + w + '×' + h +
            '. The limit is ' + LOGO.maxW + '×' + LOGO.maxH + '.'));
        }
        if (h < LOGO.minH) {
          return reject(new Error('That image is only ' + h +
            'px tall. It needs at least ' + LOGO.minH + 'px or it looks soft in the bar.'));
        }
        resolve({ dataUrl: dataUrl, width: w, height: h });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

var Branding = {
  name: 'AdminBranding',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var brand = Vue.ref('');
    var logoError = Vue.ref(null);

    Vue.watchEffect(function () {
      brand.value = store.settingValue('brand') || '';
    });

    function saveBrand() {
      store.setSetting('brand', brand.value);
    }

    var logo = Vue.computed(function () { return store.settingValue('logo'); });

    var theme = Vue.computed(function () {
      return store.settingValue('theme') || { preset: 'navy', custom: {} };
    });

    // The stored id, when it is not one this build ships. Shown rather than
    // silently corrected: a console build newer than this app is a real
    // situation and the admin should see that the salon is on something else.
    var unknownPreset = Vue.computed(function () {
      var id = theme.value && theme.value.preset;
      return id && !themePresets.presetOf(id) ? id : null;
    });

    var resolved = Vue.computed(function () { return themePresets.resolve(theme.value); });

    function setPreset(id) {
      var next = { preset: id, custom: Object.assign({}, theme.value.custom || {}) };
      store.setSetting('theme', next);
    }

    function setColor(key, value) {
      var custom = Object.assign({}, theme.value.custom || {});
      var hex = themePresets.cleanHex(value);
      // An emptied box hands that colour back to the preset, which is what the
      // console's own control does and what "custom" has to mean if it is to be
      // reversible one colour at a time.
      if (hex) custom[key] = hex;
      else delete custom[key];
      store.setSetting('theme', { preset: theme.value.preset || 'navy', custom: custom });
    }

    function clearCustom() {
      store.setSetting('theme', { preset: theme.value.preset || 'navy', custom: {} });
    }

    async function onLogoPick(evt) {
      var file = evt.target.files && evt.target.files[0];
      evt.target.value = '';       // so picking the same file twice still fires
      if (!file) return;
      logoError.value = null;
      try {
        var next = await readLogo(file);
        await store.setSetting('logo', next);
        api.toast('Logo set. Stations pick it up within a second or two.');
      } catch (err) {
        logoError.value = String(err && err.message ? err.message : err);
      }
    }

    return {
      brand: brand,
      saveBrand: saveBrand,
      isSet: store.isSet,
      clearSetting: store.clearSetting,
      setSetting: store.setSetting,
      logo: logo,
      logoError: logoError,
      logoLimits: LOGO,
      sizeLabel: sizeLabel,
      onLogoPick: onLogoPick,
      theme: theme,
      unknownPreset: unknownPreset,
      resolved: resolved,
      presets: themePresets.PRESETS,
      fields: themePresets.FIELDS,
      setPreset: setPreset,
      setColor: setColor,
      clearCustom: clearCustom,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Branding</h2>',
    '    <p class="page__lead">What the board is called and what it looks like. All of it is',
    '      saved for the whole salon and reaches every station within a second or two.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Business name</h3>',
    '      <span class="tag" :class="isSet(\'brand\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("brand") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">Shown in the top bar, in the browser tab, and anywhere the console',
    '      names the business. Empty reads <strong>Console</strong>.</p>',
    '    <div class="card__row">',
    '      <input v-model="brand" class="input" type="text" maxlength="40" spellcheck="false"',
    '        placeholder="The name across the top bar"',
    '        @change="saveBrand" @keyup.enter="$event.target.blur()" />',
    '      <confirm-button v-if="isSet(\'brand\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clearSetting(\'brand\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Logo</h3>',
    '      <span class="tag" :class="isSet(\'logo\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("logo") ? "set" : "none" }}</span>',
    '    </div>',
    '    <p class="hint">Replaces the wordmark in the top bar. {{ logoLimits.typeLabel }}, up to',
    '      {{ sizeLabel(logoLimits.maxBytes) }}, no larger than {{ logoLimits.maxW }}&times;{{ logoLimits.maxH }}',
    '      and at least {{ logoLimits.minH }}px tall. It is stored in the settings row as a data',
    '      URI and mirrored to every station, which is where the size limit comes from &mdash;',
    '      not from the database.</p>',
    '    <div class="logo-row">',
    '      <div class="logo-preview" :class="{ \'is-empty\': !logo }">',
    '        <img v-if="logo" :src="logo.dataUrl" alt="Current logo" />',
    '        <span v-else>No logo</span>',
    '      </div>',
    '      <div class="logo-row__main">',
    '        <label class="btn btn--ghost logo-pick">',
    '          <input type="file" :accept="logoLimits.types.join(\',\')" @change="onLogoPick($event)" />',
    '          <span>{{ logo ? "Replace logo" : "Choose a file" }}</span>',
    '        </label>',
    '        <confirm-button v-if="logo" label="Remove" confirm="Remove the logo" size="sm"',
    '          @confirmed="setSetting(\'logo\', null)" />',
    '        <p v-if="logo" class="hint">{{ logo.width }}&times;{{ logo.height }},',
    '          {{ Math.round(String(logo.dataUrl).length / 1024) }} KB encoded</p>',
    '      </div>',
    '    </div>',
    '    <p v-if="logoError" class="warn-line">{{ logoError }}</p>',
    '  </div>',
    '',
    '  <h3 class="page__sub">Colour scheme</h3>',
    '  <p v-if="unknownPreset" class="warn-line">This salon is on preset',
    '    <strong>{{ unknownPreset }}</strong>, which this admin build does not ship. It is a',
    '    newer console than this app knows about &mdash; the swatches below are drawn from the',
    '    default. Changing anything here would move them off it.</p>',
    '',
    '  <div class="theme-grid">',
    '    <button v-for="p in presets" :key="p.id" type="button" class="theme-card"',
    '      :class="{ \'is-on\': theme.preset === p.id }" @click="setPreset(p.id)">',
    '      <span class="theme-card__swatches">',
    '        <span :style="{ background: p.colors.primary }"></span>',
    '        <span :style="{ background: p.colors.accent }"></span>',
    '        <span :style="{ background: p.colors.action }"></span>',
    '        <span :style="{ background: p.colors.bg }"></span>',
    '      </span>',
    '      <span class="theme-card__name">{{ p.name }}</span>',
    '      <span class="theme-card__note">{{ p.note }}</span>',
    '    </button>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Custom colours</h3>',
    '      <span class="tag" :class="isSet(\'theme\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("theme") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">Each overrides the scheme above. Clear a box to hand that colour back',
    '      to the scheme. The shades that have to stay legible &mdash; the text on the header,',
    '      the badges &mdash; are derived by the console from these, so a pale header still',
    '      gets dark text.</p>',
    '    <ul class="theme-list">',
    '      <li v-for="f in fields" :key="f.key" class="theme-row">',
    '        <label class="theme-row__swatch" :style="{ background: resolved[f.key] }">',
    '          <input type="color" :value="resolved[f.key]"',
    '            @change="setColor(f.key, $event.target.value)" />',
    '        </label>',
    '        <div class="theme-row__main">',
    '          <span class="theme-row__label">{{ f.label }}',
    '            <span v-if="theme.custom && theme.custom[f.key]" class="theme-row__tag">custom</span></span>',
    '          <span class="theme-row__hint">{{ f.hint }}</span>',
    '        </div>',
    '        <input type="text" class="input input--hex" spellcheck="false" maxlength="7"',
    '          :value="(theme.custom && theme.custom[f.key]) || \'\'" :placeholder="resolved[f.key]"',
    '          @change="setColor(f.key, $event.target.value)" />',
    '      </li>',
    '    </ul>',
    '    <div class="card__row">',
    '      <button type="button" class="btn btn--quiet btn--sm" @click="clearCustom">',
    '        Back to the scheme&rsquo;s colours</button>',
    '      <confirm-button v-if="isSet(\'theme\')" label="Unset theme" size="sm" kind="quiet"',
    '        confirm="Delete the row — back to Navy &amp; Gold"',
    '        @confirmed="clearSetting(\'theme\')" />',
    '      <button type="button" class="link-btn" @click="goPage(\'advanced\')">',
    '        Edit the raw value</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'branding',
  name: 'Branding',
  group: 'Console settings',
  page: Branding
});

export { Branding };
